// externals
import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import debounce from 'lodash.debounce';

// libraries
import { Spinner } from '@interstate/components/Spinner';
import type { TextInputSize } from '@interstate/components/TextInput';
import { TextInput } from '@interstate/components/TextInput';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

// styles
import { AutocompleteContainer, SuggestionItem, SuggestionList } from './Autocomplete.style';

// selectors
import { deferredSelect, useSelectOnFocusEnabledStrict } from '../selectOnFocus/selectOnFocus';
import type { SelectOnFocusSelector } from '../selectOnFocus/selectOnFocus';

// types
import type { AutocompleteOptions } from '@makemydeal/dr-shared-types';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';

export interface AutocompleteProps {
    'data-testid'?: string;
    errorMessage?: string;
    hasError?: boolean;
    id?: string;
    isLoading?: boolean;
    label?: string;
    displayLabel?: boolean;
    inputSuffix?: React.ReactNode;
    menuOptions: AutocompleteOptions[];
    placeholder?: string;
    position?: 'top' | 'bottom';
    size?: TextInputSize;
    width?: string;
    value?: string;
    onSearch: (input: string) => void;
    onSelect: (input: AutocompleteOptions) => void;
    onChange: (value: string) => void;
    maxLength?: number;
    disabled?: boolean;
    name?: string;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: InterstateOnChangeEvent<string>) => void;
    onKeyDown?: (e: any) => void;
    renderDropdownPortal?: boolean;
    customErrorMessages?: Record<string, string>;
    selectOnFocusSelector?: SelectOnFocusSelector;
}

const Autocomplete: React.FC<AutocompleteProps> = (props) => {
    const {
        'data-testid': dataTestId = 'autocomplete',
        errorMessage = '',
        hasError = false,
        id,
        value = '',
        isLoading = false,
        label = '',
        displayLabel,
        inputSuffix,
        menuOptions,
        placeholder = 'Search...',
        position = 'bottom',
        size,
        width,
        onSearch,
        onSelect,
        onChange,
        maxLength,
        disabled,
        name,
        onFocus,
        onBlur,
        onKeyDown,
        renderDropdownPortal = false,
        customErrorMessages,
        selectOnFocusSelector
    } = props;

    const isAutoSelectEnabled = useSelectOnFocusEnabledStrict(selectOnFocusSelector);

    const [inputValue, setInputValue] = useState<string>(value);
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(-1);

    const theme = useInterstateTheme();

    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionListRef = useRef<HTMLUListElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const onSearchRef = useRef(onSearch);
    const lastBlurValueRef = useRef<string>(value);

    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    const debouncedSearch = useCallback(
        debounce((searchTerm: string) => {
            setActiveSuggestionIndex(-1);
            onSearchRef.current(searchTerm);
        }, 300),
        [] // Empty deps - debounced function is stable, uses ref for latest callback
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        setInputValue(value);
        // Also update lastBlurValueRef when value changes from parent (e.g., auto-populated from selection)
        // This prevents unnecessary API calls when focusing on a field that was populated externally
        lastBlurValueRef.current = value;
    }, [value]);

    const getDropdownPosition = () => {
        if (!renderDropdownPortal || !containerRef.current) return null;

        /*istanbul ignore next*/
        const portalContainer = containerRef.current?.closest('[data-portal-container="autocomplete"]');
        if (!portalContainer) return null;

        const inputRect = containerRef.current.getBoundingClientRect();
        const containerRect = portalContainer.getBoundingClientRect();

        return {
            top: inputRect.bottom - containerRect.top,
            left: inputRect.left - containerRect.left,
            width: inputRect.width
        };
    };

    const handleInputChange = (e: any) => {
        const value = e.target.value;
        setInputValue(value);
        onChange(value);

        if (value.length >= 3) {
            setShowSuggestions(true);
            debouncedSearch(value);
        } else {
            setShowSuggestions(false);
            debouncedSearch(value);
        }
    };

    const handleSelectOption = (option: AutocompleteOptions) => {
        setInputValue(option.label);
        setShowSuggestions(false);
        onSelect(option);
    };

    const handleKeyDown = (e: any) => {
        if (!showSuggestions || menuOptions.length === 0) {
            onKeyDown?.(e);
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveSuggestionIndex((prevIndex) => (prevIndex < menuOptions.length - 1 ? prevIndex + 1 : 0));
                break;

            case 'ArrowUp':
                e.preventDefault();
                setActiveSuggestionIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : menuOptions.length - 1));
                break;

            case 'Enter':
            case 'Tab':
                e.preventDefault();
                if (menuOptions[activeSuggestionIndex]) {
                    handleSelectOption(menuOptions[activeSuggestionIndex]);
                } else {
                    onKeyDown?.(e);
                }
                break;

            case 'Escape':
                e.preventDefault();
                setShowSuggestions(false);
                break;

            default:
                onKeyDown?.(e);
                break;
        }
    };

    useEffect(() => {
        if (showSuggestions && suggestionListRef.current) {
            const activeElement = suggestionListRef.current.children[activeSuggestionIndex];
            if (activeElement) {
                (activeElement as HTMLElement).scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            }
        }
    }, [activeSuggestionIndex, showSuggestions]);

    const highlightInput = (label: string, input: string) => {
        const escapedInput = input.replace(/[.*+?^${}()|\\[\]-]/g, '\\$&');
        const regex = new RegExp(`(${escapedInput})`, 'gi');
        const parts = label.split(regex).filter(Boolean);

        return parts.map((part: string) => {
            const shouldHighlight = input.toLowerCase() === part.toLowerCase();
            return (
                <span
                    key={part}
                    style={{
                        fontWeight: shouldHighlight ? 600 : 400
                    }}
                >
                    {part}
                </span>
            );
        });
    };

    const getDropdownContent = () => {
        if (!showSuggestions || !menuOptions.length || inputValue.length < 3) {
            return null;
        }

        const dropdownPosition = getDropdownPosition();

        const dropdown = (
            <SuggestionList
                data-testid={`${dataTestId}-suggestion-list`}
                ref={suggestionListRef}
                role="menu"
                $position={position}
                $renderDropdownPortal={renderDropdownPortal}
                $dropdownPosition={dropdownPosition || undefined}
                theme={theme}
                onMouseDown={(e) => e.preventDefault()}
            >
                {menuOptions.map((option, index) => {
                    return (
                        <SuggestionItem
                            key={`${option.label}-${index}`}
                            $active={index === activeSuggestionIndex}
                            role="menuitem"
                            onClick={() => handleSelectOption(option)}
                            onMouseEnter={() => setActiveSuggestionIndex(index)}
                        >
                            <p>{highlightInput(option.label, inputValue)}</p>
                            {option.additionalText && <p>{option.additionalText}</p>}
                        </SuggestionItem>
                    );
                })}
            </SuggestionList>
        );

        if (renderDropdownPortal) {
            /*istanbul ignore next*/
            const portalContainer = containerRef.current?.closest('[data-portal-container="autocomplete"]');
            return portalContainer && dropdownPosition ? createPortal(dropdown, portalContainer) : null;
        }

        return dropdown;
    };

    return (
        <AutocompleteContainer id={id} data-testid={dataTestId} ref={containerRef}>
            <TextInput
                errorMessage={errorMessage}
                hasError={hasError}
                inputSuffix={isLoading ? <Spinner data-testid={`${dataTestId}-spinner`} size={30} /> : inputSuffix ?? false}
                label={label}
                {...(displayLabel !== undefined && { displayLabel })}
                name={name || 'autocomplete-input'}
                data-testid={`${dataTestId}-input`}
                placeholder={placeholder}
                ref={inputRef}
                size={size}
                sx={{ width: width ?? '100%' }}
                value={inputValue}
                onBlur={(e) =>
                    setTimeout(() => {
                        setShowSuggestions(false);
                        debouncedSearch.cancel();
                        lastBlurValueRef.current = inputValue;
                        onBlur?.(e);
                    }, 300)
                }
                onChange={handleInputChange}
                onFocus={(e) => {
                    // Only trigger search on focus if the value has changed since last blur
                    // This prevents unnecessary API calls when clicking back on an already-selected value
                    if (inputValue !== lastBlurValueRef.current) {
                        handleInputChange(e);
                    }
                    onFocus?.(e as React.FocusEvent<HTMLInputElement>);
                    if (isAutoSelectEnabled) deferredSelect(e.currentTarget);
                }}
                onKeyDown={(e) => {
                    // Allow Tab key to move focus naturally
                    if (e.key === 'Tab' && showSuggestions && activeSuggestionIndex === -1) {
                        setShowSuggestions(false);
                        return;
                    }
                    handleKeyDown(e);
                }}
                maxLength={maxLength}
                disabled={disabled}
                customErrorMessages={customErrorMessages}
            />

            {getDropdownContent()}
        </AutocompleteContainer>
    );
};

export default Autocomplete;
